import { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    InputAdornment,
    MenuItem,
    OutlinedInput,
    Select,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from '@mui/material';
import CompareArrowsOutlinedIcon from '@mui/icons-material/CompareArrowsOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import FindInPageOutlinedIcon from '@mui/icons-material/FindInPageOutlined';
import axiosInstance from '../../../helpers/axios';
import { formatCurrency } from '../utils/format';
import { getReconciliationStatusMeta, getDiscrepancyValues } from '../utils/reconciliationStatus';
import { MONTH_NAMES, monthLabel } from '../utils/month';
import { fibeTokens } from '../../../theme';
import PaginationTable from '../../status/PaginationTable';

const PAGE_SIZE = 15;

function formatDiscrepancyValue(value, valueField) {
    if (value == null) {
        return '—';
    }
    return valueField === 'tenure' ? `${value}m` : formatCurrency(value);
}

const BADGE_CHIP_COLOR = {
    'badge-green': 'success',
    'badge-yellow': 'warning',
    'badge-red': 'error',
    'badge-blue': 'info',
    'badge-gray': 'default',
};

const MENU_PROPS = {
    PaperProps: {
        sx: {
            '& .MuiMenuItem-root': {
                fontFamily: fibeTokens.font.family,
                fontSize: 12.5,
                fontWeight: 400,
                minHeight: 'auto',
                padding: '8px 12px',
            },
        },
    },
};

function ErrorBanner({ title, message, onRetry, retryLabel = 'Retry' }) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '14px',
                background: fibeTokens.color.dangerTint,
                border: `1px solid ${fibeTokens.color.dangerBorder}`,
                borderRadius: `${fibeTokens.density.cardRadius}px`,
                padding: fibeTokens.density.cardPadding,
            }}
        >
            <Box>
                <Box sx={{ fontFamily: fibeTokens.font.family, fontWeight: 600, fontSize: 13, color: fibeTokens.color.danger }}>
                    {title}
                </Box>
                <Box sx={{ ...fibeTokens.font.helper, fontFamily: fibeTokens.font.family, color: fibeTokens.color.danger, marginTop: '4px' }}>
                    {message}
                </Box>
            </Box>
            {onRetry && (
                <Box sx={{ flex: 'none' }}>
                    <Button variant="fibeSecondary" size="small" onClick={onRetry}>{retryLabel}</Button>
                </Box>
            )}
        </Box>
    );
}

function fieldLabelSx() {
    return { display: 'block', fontFamily: fibeTokens.font.family, ...fibeTokens.font.fieldLabel, marginBottom: '6px' };
}

export function UploadReconciliationForm({ onUploaded }) {
    const now = new Date();
    const [billingMonth, setBillingMonth] = useState(now.getMonth() + 1);
    const [billingYear, setBillingYear] = useState(now.getFullYear());
    const [selectedFile, setSelectedFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [fileInputKey, setFileInputKey] = useState(0);

    const handleUpload = () => {
        if (!selectedFile) {
            setUploadError('Choose a file first.');
            return;
        }

        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('billingMonth', billingMonth);
        formData.append('billingYear', billingYear);

        setIsUploading(true);
        setUploadError(null);

        axiosInstance
            .post('/fibe-billing/reconciliation/upload', formData)
            .then(() => {
                setSelectedFile(null);
                setFileInputKey((key) => key + 1);
                onUploaded();
            })
            .catch((error) => {
                setUploadError(error.response?.data?.message ?? 'Upload failed.');
            })
            .finally(() => setIsUploading(false));
    };

    return (
        <Box
            sx={{
                background: fibeTokens.color.surface,
                border: `1px solid ${fibeTokens.color.border}`,
                borderRadius: `${fibeTokens.density.cardRadius}px`,
                boxShadow: fibeTokens.density.cardShadow,
                padding: fibeTokens.density.cardPadding,
                marginBottom: '14px',
            }}
        >
            <Box component="h2" sx={{ margin: 0, fontFamily: fibeTokens.font.family, ...fibeTokens.font.sectionHeading, color: fibeTokens.color.textPrimary }}>
                Upload Fibe Monthly Disbursement File
            </Box>
            <Box sx={{ margin: '4px 0 14px', fontFamily: fibeTokens.font.family, ...fibeTokens.font.helper }}>
                Select the billing month and upload the disbursement file to reconcile.
            </Box>

            <Box sx={{ display: 'flex', gap: '12px', marginBottom: '14px' }}>
                <Box sx={{ width: 200 }}>
                    <Box component="label" sx={fieldLabelSx()}>Billing Month</Box>
                    <Select
                        size="fibeMd"
                        fullWidth
                        value={billingMonth}
                        onChange={(e) => setBillingMonth(Number(e.target.value))}
                        MenuProps={MENU_PROPS}
                        renderValue={(value) => (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <CalendarMonthOutlinedIcon sx={{ fontSize: 17, color: fibeTokens.color.textMuted }} />
                                {MONTH_NAMES[value - 1]}
                            </Box>
                        )}
                    >
                        {MONTH_NAMES.map((name, index) => (
                            <MenuItem key={name} value={index + 1}>{name}</MenuItem>
                        ))}
                    </Select>
                </Box>
                <Box sx={{ width: 140 }}>
                    <Box component="label" sx={fieldLabelSx()}>Billing Year</Box>
                    <OutlinedInput
                        size="fibeMd"
                        fullWidth
                        type="number"
                        value={billingYear}
                        onChange={(e) => setBillingYear(Number(e.target.value))}
                        startAdornment={(
                            <InputAdornment position="start">
                                <CalendarMonthOutlinedIcon sx={{ fontSize: 17, color: fibeTokens.color.textMuted }} />
                            </InputAdornment>
                        )}
                    />
                </Box>
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px',
                    border: `1.5px dashed ${fibeTokens.color.borderStrong}`,
                    borderRadius: '12px',
                    background: fibeTokens.color.pageBg,
                    padding: '14px 16px',
                }}
            >
                <Box component="label" htmlFor="reconciliation-file-input" sx={{ display: 'flex', alignItems: 'center', gap: '14px', flex: 1, minWidth: 0, cursor: 'pointer' }}>
                    <Box
                        sx={{
                            width: 42,
                            height: 42,
                            flex: 'none',
                            borderRadius: '11px',
                            background: fibeTokens.color.primaryTint,
                            color: fibeTokens.color.primary,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <CloudUploadOutlinedIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ fontFamily: fibeTokens.font.family, fontWeight: 600, fontSize: 13.5, color: fibeTokens.color.textSecondary }}>
                            Drag & drop the disbursement file here, or <Box component="span" sx={{ color: fibeTokens.color.primary }}>browse</Box>
                        </Box>
                        <Box sx={{ fontFamily: fibeTokens.font.family, ...fibeTokens.font.helper, marginTop: '4px' }}>
                            Supported formats: .csv, .xlsx, .xls &nbsp;•&nbsp; Max file size: 10MB
                        </Box>
                        <Box sx={{ fontFamily: fibeTokens.font.family, ...fibeTokens.font.helper, marginTop: '4px' }}>
                            {selectedFile ? selectedFile.name : 'No file chosen'}
                        </Box>
                    </Box>
                </Box>
                <input
                    id="reconciliation-file-input"
                    key={fileInputKey}
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={(e) => setSelectedFile(e.target.files[0] ?? null)}
                    style={{ display: 'none' }}
                />
                <Button
                    variant="fibePrimary"
                    onClick={handleUpload}
                    disabled={isUploading}
                    startIcon={!isUploading && <UploadFileOutlinedIcon sx={{ fontSize: 16 }} />}
                    sx={{ flex: 'none' }}
                >
                    {isUploading ? 'Uploading...' : 'Upload & Reconcile'}
                </Button>
            </Box>

            {uploadError && (
                <Box sx={{ marginTop: '14px' }}>
                    <ErrorBanner title="Could not upload file" message={uploadError} />
                </Box>
            )}
        </Box>
    );
}

function ReconciliationEmptyState() {
    return (
        <Box
            sx={{
                textAlign: 'center',
                background: fibeTokens.color.surface,
                border: `1px solid ${fibeTokens.color.border}`,
                borderRadius: `${fibeTokens.density.cardRadius}px`,
                boxShadow: fibeTokens.density.cardShadow,
                padding: '40px 18px',
            }}
        >
            <Box
                sx={{
                    width: 42,
                    height: 42,
                    margin: '0 auto 12px',
                    borderRadius: '12px',
                    background: fibeTokens.color.surface,
                    border: `1px solid ${fibeTokens.color.border}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: fibeTokens.color.textDisabled,
                }}
            >
                <CompareArrowsOutlinedIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box sx={{ fontFamily: fibeTokens.font.family, fontWeight: 600, fontSize: 13, color: fibeTokens.color.textPrimary }}>
                No reconciliation has been uploaded yet.
            </Box>
            <Box sx={{ fontFamily: fibeTokens.font.family, fontWeight: 400, fontSize: 12, lineHeight: 1.6, color: fibeTokens.color.textMuted, margin: '5px 0 0' }}>
                Upload a Fibe disbursement file above to see results here.
            </Box>
        </Box>
    );
}

const RECONCILIATION_SKELETON_COLUMNS = [
    { width: 90 },
    { width: 78 },
    { width: 96, pill: true },
    { width: 130 },
    { width: 70 },
    { width: 70 },
];

function ReconciliationResultsSkeleton() {
    return (
        <Box
            sx={{
                background: fibeTokens.color.surface,
                border: `1px solid ${fibeTokens.color.border}`,
                borderRadius: `${fibeTokens.density.cardRadius}px`,
                boxShadow: fibeTokens.density.cardShadow,
                overflow: 'hidden',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', padding: fibeTokens.density.cardHeaderPadding, borderBottom: `1px solid ${fibeTokens.color.border}` }}>
                <Skeleton variant="text" width={220} height={16} />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px', padding: '16px 18px 14px' }}>
                {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} variant="rounded" height={58} sx={{ borderRadius: '10px' }} />
                ))}
            </Box>
            {Array.from({ length: 5 }).map((_, rowIndex) => (
                <Box
                    key={rowIndex}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        height: `${fibeTokens.density.tableRowHeight}px`,
                        padding: '0 14px',
                        borderBottom: rowIndex < 4 ? `1px solid ${fibeTokens.color.border}` : 'none',
                    }}
                >
                    {RECONCILIATION_SKELETON_COLUMNS.map((col, colIndex) => (
                        <Skeleton
                            key={colIndex}
                            variant={col.pill ? 'rounded' : 'text'}
                            width={col.width}
                            height={col.pill ? 18 : 12}
                            sx={{ borderRadius: col.pill ? '999px' : undefined, flex: 'none' }}
                        />
                    ))}
                </Box>
            ))}
        </Box>
    );
}

function PageHeader({ isLoading, onRefresh }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <Box
                    sx={{
                        width: fibeTokens.iconContainer.size,
                        height: fibeTokens.iconContainer.size,
                        flex: 'none',
                        borderRadius: `${fibeTokens.iconContainer.radius}px`,
                        background: fibeTokens.iconContainer.tones.red.background,
                        color: fibeTokens.iconContainer.tones.red.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <CompareArrowsOutlinedIcon sx={{ fontSize: 22 }} />
                </Box>
                <Box>
                    <Box component="h1" sx={{ margin: 0, fontFamily: fibeTokens.font.family, ...fibeTokens.font.pageTitle, color: fibeTokens.color.textPrimary }}>
                        Disbursement Reconciliation
                    </Box>
                    <Box sx={{ margin: '5px 0 0', fontFamily: fibeTokens.font.family, ...fibeTokens.font.helper }}>
                        Match Fibe's monthly disbursement file against FeeMonk records
                    </Box>
                </Box>
            </Box>
            <Button
                variant="fibeSecondary"
                disabled={isLoading}
                onClick={onRefresh}
                startIcon={isLoading ? <CircularProgress size={14} color="inherit" /> : <RefreshOutlinedIcon sx={{ fontSize: 16 }} />}
            >
                {isLoading ? 'Refreshing...' : 'Refresh'}
            </Button>
        </Box>
    );
}

function ReconciliationTab() {
    const [latestBatch, setLatestBatch] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState(null);
    const [showTable, setShowTable] = useState(true);
    const [page, setPage] = useState(1);

    const loadLatestBatch = useCallback((pageNo) => {
        setIsLoading(true);
        setErrorMessage(null);

        axiosInstance
            .get('/fibe-billing/reconciliation/latest', { params: { pageNo, perPage: PAGE_SIZE } })
            .then((response) => setLatestBatch(response.data.data))
            .catch(() => setErrorMessage('Could not load the latest reconciliation batch.'))
            .finally(() => setIsLoading(false));
    }, []);

    useEffect(() => {
        loadLatestBatch(page);
    }, [page, loadLatestBatch]);

    const handleUploaded = () => {
        setPage(1);
        loadLatestBatch(1);
    };

    return (
        <div>
            <PageHeader isLoading={isLoading} onRefresh={() => loadLatestBatch(page)} />

            <UploadReconciliationForm onUploaded={handleUploaded} />

            {isLoading ? (
                <ReconciliationResultsSkeleton />
            ) : errorMessage ? (
                <ErrorBanner title="Could not load the latest reconciliation batch" message={errorMessage} onRetry={() => loadLatestBatch(page)} />
            ) : !latestBatch ? (
                <ReconciliationEmptyState />
            ) : (
                <Box
                    sx={{
                        background: fibeTokens.color.surface,
                        border: `1px solid ${fibeTokens.color.border}`,
                        borderRadius: `${fibeTokens.density.cardRadius}px`,
                        boxShadow: fibeTokens.density.cardShadow,
                        overflow: 'hidden',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', padding: fibeTokens.density.cardHeaderPadding, borderBottom: `1px solid ${fibeTokens.color.border}`, flexWrap: 'wrap' }}>
                        <Box sx={{ width: '3px', height: '20px', borderRadius: '9px', background: fibeTokens.color.primary, flex: 'none' }} />
                        <Box component="h2" sx={{ margin: 0, fontFamily: fibeTokens.font.family, ...fibeTokens.font.sectionHeading, color: fibeTokens.color.textPrimary }}>
                            Last Reconciliation — {monthLabel(latestBatch.billingMonth, latestBatch.billingYear)}
                        </Box>
                        <Chip variant="fibePill" color="success" label="Completed" />
                        <Box sx={{ flex: 1 }} />
                        <Button
                            variant="fibeSecondary"
                            size="small"
                            startIcon={<VisibilityOutlinedIcon sx={{ fontSize: 15 }} />}
                            onClick={() => setShowTable((value) => !value)}
                        >
                            {showTable ? 'Hide Summary' : 'View Summary'}
                        </Button>
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px', padding: '16px 18px 14px' }}>
                        {[
                            { key: 'totalRows', label: 'Total Rows', value: latestBatch.counts.totalRows, Icon: DescriptionOutlinedIcon, iconTone: 'red' },
                            { key: 'matched', label: 'Matched', value: latestBatch.counts.matched, tone: fibeTokens.color.success, Icon: CheckCircleOutlinedIcon, iconTone: 'green' },
                            { key: 'missingFromFeemonk', label: 'Missing (FeeMonk)', value: latestBatch.counts.missingFromFeemonk, tone: fibeTokens.color.warning, Icon: WarningAmberOutlinedIcon, iconTone: 'amber' },
                            { key: 'missingFromFibe', label: 'Missing (Fibe)', value: latestBatch.counts.missingFromFibe, tone: fibeTokens.color.danger, Icon: WarningAmberOutlinedIcon, iconTone: 'red' },
                            { key: 'mismatched', label: 'Mismatched', value: latestBatch.counts.mismatched, Icon: FindInPageOutlinedIcon, iconTone: 'blue' },
                        ].map((stat) => (
                            <Box
                                key={stat.key}
                                sx={{ border: `1px solid ${fibeTokens.color.border}`, borderRadius: '10px', padding: '8px 10px', background: fibeTokens.color.rowHover }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                                    <Box sx={{ fontFamily: fibeTokens.font.family, fontWeight: 600, fontSize: 10, color: fibeTokens.color.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                        {stat.label}
                                    </Box>
                                    <Box
                                        sx={{
                                            width: 30,
                                            height: 30,
                                            flex: 'none',
                                            borderRadius: '9px',
                                            background: fibeTokens.iconContainer.tones[stat.iconTone].background,
                                            color: fibeTokens.iconContainer.tones[stat.iconTone].color,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <stat.Icon sx={{ fontSize: 16 }} />
                                    </Box>
                                </Box>
                                <Box sx={{ fontFamily: fibeTokens.font.family, fontWeight: 600, fontSize: 18, color: stat.tone ?? fibeTokens.color.textPrimary, marginTop: '6px', letterSpacing: '-0.015em' }}>
                                    {stat.value}
                                </Box>
                            </Box>
                        ))}
                    </Box>

                    {showTable && <Box sx={{ overflowX: 'auto' }}>
                        <Table sx={{ width: '100%', minWidth: '760px', tableLayout: 'fixed', borderCollapse: 'collapse' }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell size="fibe" variant="head" align="left" sx={{ width: '15%' }}>Fibe Loan ID</TableCell>
                                    <TableCell size="fibe" variant="head" align="left" sx={{ width: '15%' }}>App ID</TableCell>
                                    <TableCell size="fibe" variant="head" align="left" sx={{ width: '15%' }}>Status</TableCell>
                                    <TableCell size="fibe" variant="head" align="left" sx={{ width: '25%' }}>Discrepancy</TableCell>
                                    <TableCell size="fibe" variant="head" align="right" sx={{ width: '15%' }}>Fibe Value</TableCell>
                                    <TableCell size="fibe" variant="head" align="right" sx={{ width: '15%' }}>FeeMonk Value</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {latestBatch.records.map((record) => {
                                    const meta = getReconciliationStatusMeta(record.status);
                                    const { fibeValue, feemonkValue, valueField } = getDiscrepancyValues(record);
                                    return (
                                        <TableRow key={record.id} hover>
                                            <TableCell
                                                size="fibe"
                                                variant="body"
                                                align="left"
                                                sx={{ width: '15%' }}
                                            >
                                                {record.fibeLoanId ?? '—'}
                                            </TableCell>
                                            <TableCell
                                                size="fibe"
                                                variant="body"
                                                align="left"
                                                sx={{ width: '15%' }}
                                            >
                                                {record.applicationId ?? '—'}
                                            </TableCell>
                                            <TableCell size="fibe" variant="body" align="left" sx={{ width: '15%' }}>
                                                <Chip variant="fibePill" color={BADGE_CHIP_COLOR[meta.badgeClass] ?? 'default'} label={meta.label} />
                                            </TableCell>
                                            <TableCell size="fibe" variant="body" align="left" sx={{ color: fibeTokens.color.textMuted, width: '25%' }}>
                                                {meta.discrepancy}
                                            </TableCell>
                                            <TableCell size="fibe" variant="body" align="right" sx={{ fontVariantNumeric: 'tabular-nums', width: '15%' }}>
                                                {formatDiscrepancyValue(fibeValue, valueField)}
                                            </TableCell>
                                            <TableCell size="fibe" variant="body" align="right" sx={{ fontVariantNumeric: 'tabular-nums', width: '15%' }}>
                                                {formatDiscrepancyValue(feemonkValue, valueField)}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </Box>}
                </Box>
            )}

            {latestBatch && showTable && (
                <Box sx={{ marginTop: '14px' }}>
                    <PaginationTable
                        key={latestBatch.reconciliationBatchId}
                        startPage={page}
                        rowsPerPage={latestBatch.pagination?.perPage ?? PAGE_SIZE}
                        totalRows={latestBatch.counts.totalRows}
                        paginate={setPage}
                    />
                </Box>
            )}
        </div>
    );
}

export default ReconciliationTab;